import { Button, ButtonGroup } from "@components/button";
import {
    ISmartBankAccountFilterCustomData
} from "@components/smart/smartBankAccountFilter/SmartBankAccountFilter.utils";
import { ActionType, addNewLineItem, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import {
    BankStatementEntity,
    BankTransactionEntity,
    IBankStatementEntity,
    IBankTransactionEntity,
    IFileMetadataEntity,
    ILoanStatementEntity
} from "@odata/GeneratedEntityTypes";
import { BankTransactionTypeCode, DocumentTypeCode, VatStatusCode } from "@odata/GeneratedEnums";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { isEmptyArray, roundToDecimalPlaces } from "@utils/general";
import { cloneDeep } from "lodash";
import React from "react";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { ModelEvent } from "../../../model/Model";
import BindingContext, { IEntity } from "../../../odata/BindingContext";
import { getUtcDate } from "../../../types/Date";
import { DEFAULT_SCALE_CURRENCY_VALUE } from "../../../types/Number";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import { loadExchangeRate, setLockBreadcrumbs } from "../../documents/Document.utils";
import { canChangeNumberRange, loadAndStoreRanges } from "../../numberRange/NumberRange.utils";
import { getCorrent6Digit } from "../bankTransactions/BankTransactions.utils";
import {
    calculateAndStoreTransactionCount,
    getBankAccountBalanceForDate,
    handleApplyBankStatementAttachment,
    onDateFromChange,
    recalculateBalance,
    refreshBankStatementNumberRange,
    refreshExchangesRates
} from "./BankStatements.utils";
import {
    APPLY_BANKFILE_IMPORT,
    ClosingBalancePath,
    GROUP_TRANSACTIONS_ID,
    OpeningBalancePath
} from "./BankStatementsDef";

interface IProps extends IFormViewProps<TStatementEntity, ISmartBankAccountFilterCustomData> {
    accountId?: number;
}

type TStatementEntity = ILoanStatementEntity | IBankStatementEntity;

class BankStatementsFormView extends FormViewForExtend<TStatementEntity, IProps> {
    static contextType = AppContext;

    documentType = DocumentTypeCode.BankTransaction;

    shouldDisableSaveButton = (): boolean => {
        return this.props.storage.isDisabled || this.entity.Locks?.length > 0;
    };

    getAccounts = (): IEntity[] => {
        return this.context.getCompanyBankAccounts(true) || [];
    }

    onAfterLoad = async () => {
        const { accountId, storage } = this.props;

        await loadAndStoreRanges(storage, true);

        if (storage.data.bindingContext.isNew()) {
            if (isEmptyArray(this.entity.Transactions)) {
                addNewLineItem(storage, "Transactions", GROUP_TRANSACTIONS_ID, [], {
                    DateBankTransaction: getUtcDate()
                });
            }

            const accounts = this.getAccounts() || [];
            const defAcc = accountId ? accounts.find(acc => acc.Id === accountId) : accounts.find(acc => acc.IsDefault);

            if (defAcc) {
                this.handleChange({
                    value: defAcc.Id,
                    bindingContext: storage.data.bindingContext.navigate("BankAccount"),
                    triggerAdditionalTasks: true,
                    additionalData: defAcc
                });
                refreshBankStatementNumberRange(storage, defAcc?.BankStatementNumberRangeDefinition?.Id);
            }
        } else {
            // call only for saved entities, otherwise handleChange ^^^ will trigger the call
            await this.getInitialBalance();
            calculateAndStoreTransactionCount(storage);
        }

        return super.onAfterLoad();
    };

    async getInitialBalance() {
        const { storage } = this.props;
        const { DateFrom, BankAccount, Id } = storage.data.entity;
        if (!DateFrom || !BankAccount?.Id) {
            return;
        }
        const initialBalance = await getBankAccountBalanceForDate(storage.oData, this.documentType, storage.context, DateFrom, BankAccount.Id, Id);
        storage.setCustomData({ initialBalance });
        storage.addActiveField(storage.data.bindingContext.navigate(OpeningBalancePath));
        storage.addActiveField(storage.data.bindingContext.navigate(ClosingBalancePath));
    }

    handleCustomFileAction = async ({
                                        file, action
                                    }: { file: IFileMetadataEntity, action: string, attachId: number }) => {
        if (action === APPLY_BANKFILE_IMPORT) {
            const response = await handleApplyBankStatementAttachment(this.props.storage, file);
            if (response !== true) {
                this.props.storage.setFormAlert(response);
            }
            this.forceUpdate();
        }
    };

    componentDidMount(): void {
        super.componentDidMount();
        this.props.storage.emitter.on(ModelEvent.CustomFileAction, this.handleCustomFileAction);

        this.registerHeader();
    }

    componentWillUnmount() {
        this.props.storage.emitter.off(ModelEvent.CustomFileAction, this.handleCustomFileAction);
        super.componentWillUnmount();
    }

    componentDidUpdate(): void {
        this.registerHeader();
    }

    registerHeader = (): void => {
        if (this.props.storage.data.bindingContext) {
            const numberOursBc = this.props.storage.data.bindingContext.navigate(BankStatementEntity.NumberOurs);
            this.props.storage.addCustomRef(this._refHeader.current, numberOursBc);
        }
    };

    setItemExchangeRate = async (itemBc: BindingContext, date: Date): Promise<void> => {
        const currencyCode = this.entity.BankAccount?.TransactionCurrency?.Code;
        const rate = await loadExchangeRate(this.props.storage, currencyCode, date);
        if (rate) {
            this.props.storage.setValue(itemBc.navigate("ExchangeRatePerUnit"), rate);
            this.props.storage.refreshFields();
        }
    }

    handleDateBankChange = async (args: ISmartFieldChange) => {
        if (args.bindingContext.getPath() === BankTransactionEntity.DateBankTransaction && args.triggerAdditionalTasks) {
            const date = this.props.storage.getValue(args.bindingContext);
            if (!date) {
                return;
            }
            await this.setItemExchangeRate(args.bindingContext.getParent(), date);
        }
    };

    setBreadcrumbs = (): void => {
        const { storage } = this.props;
        const title = storage.data.definition.getItemBreadCrumbText(storage);
        setLockBreadcrumbs({
            storage,
            title,
            onBeforeSave: this.props.onBeforeSave,
            onSaveFail: this.props.onSaveFail,
            onTableRefreshNeeded: this.props.onTableRefreshNeeded,
            onAfterSave: this.props.onAfterSave,
            context: this.context
        });
    };

    handleDateFromChange = (args: ISmartFieldChange) => {
        if (args.bindingContext.getPath() === "DateFrom" && args.triggerAdditionalTasks) {
            onDateFromChange(this.props.storage, args.value as Date);
        }
    };

    handleBankAccountChange = (e: ISmartFieldChange) => {
        const { storage } = this.props;
        if (e.bindingContext.getPath() === "BankAccount" && e.triggerAdditionalTasks) {
            if (canChangeNumberRange(storage)) {
                refreshBankStatementNumberRange(this.props.storage, e.additionalData?.BankStatementNumberRangeDefinition?.Id);
            }
            const oldCurrency = this.entity.BankAccount?.TransactionCurrency?.Code;
            const newCurrency = e.additionalData.TransactionCurrency?.Code;
            if (newCurrency !== oldCurrency) {
                for (const transaction of storage.data.entity.Transactions || []) {
                    transaction.TransactionCurrency = {
                        Code: newCurrency
                    };
                }

                refreshExchangesRates(storage, newCurrency);
            }

            const wasSet = this.entity.BankAccount?.Id;
            if (!wasSet) {
                storage.refresh();
            }
        }
    };

    handleNumberRangeChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === "NumberRange") {
            this.props.storage.data.entity.NumberRange = e.value ? e.additionalData : null;
        }
    };

    handleNumberOursChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === BankStatementEntity.NumberOurs) {
            // replace values of NumberOurs in all transactions
            const { storage } = this.props;
            const { entity, bindingContext } = storage.data;
            if (entity.Transactions) {
                for (const item of bindingContext.iterateNavigation(BankStatementEntity.Transactions, entity.Transactions)) {
                    const bc = item.bindingContext.navigate(BankTransactionEntity.NumberOurs);
                    const numberOurs = storage.getValue(bc);
                    if (numberOurs) {
                        const newNumber = numberOurs.replace(/.*-/, `${e.value}-`);
                        storage.clearAndSetValue(bc, newNumber);
                    }
                }
            }
            storage.emitter.emit(ModelEvent.RecalculateFastEntryList);
        }
    };


    handleChange = (e: ISmartFieldChange) => {
        const { storage } = this.props;

        this.handleBankAccountChange(e);
        storage.handleChange(e);

        this.handleNumberRangeChange(e);
        this.handleNumberOursChange(e);

        // after change !
        this.handleDateFromChange(e);

        const path = e.bindingContext.getPath();
        // initial balance has probably changed as we are changing BankAccount or Statement Date
        if (["BankAccount", "DateFrom"].includes(path) && e.triggerAdditionalTasks) {
            this.getInitialBalance()
                .then(() => storage.refreshFields(true));
        }
        if (path === "TransactionAmount") {
        }
        storage.refreshFields(e.triggerAdditionalTasks);
    };

    recalculate = (args: ISmartFieldChange) => {
        if (args.bindingContext.getPath() === "TransactionAmount") {
            recalculateBalance(this.props.storage);
        }

        const currentTranCount = this.props.storage.data.entity.Transactions?.length || 0;
        const calculatedCount = this.props.storage.getCustomData().transactionCount;

        if (currentTranCount !== calculatedCount) {
            calculateAndStoreTransactionCount(this.props.storage);
            this.props.storage.addActiveField(this.props.storage.data.bindingContext.navigate(BindingContext.localContext("TransactionCount")));
        }
    }

    handleLineItemsChange = (e: ISmartFieldChange): void => {
        this.props.storage.handleLineItemsChange(e);
        this.handleDateBankChange(e);

        this.recalculate(e);
        this.props.storage.refreshFields();
    };

    handleLineItemsAction = (args: ISmartFastEntriesActionEvent): void => {
        const storage = this.props.storage;

        if (args.actionType === ActionType.Add) {
            const [item] = args.affectedItems as IBankTransactionEntity[];
            this.setItemExchangeRate(storage.data.bindingContext.navigate(BankStatementEntity.Transactions).addKey(item), item.DateBankTransaction);
            item.TransactionCurrencyCode = this.entity.BankAccount?.TransactionCurrencyCode;
        }

        storage.handleLineItemsAction(args);

        if ([ActionType.Remove, ActionType.Clone].includes(args.actionType)) {
            recalculateBalance(storage);
        }

        calculateAndStoreTransactionCount(storage);
        storage.refresh();
    };

    setCorrectTransactionType = (transaction: IBankTransactionEntity): void => {
        transaction.BankTransactionTypeCode = transaction.TransactionAmount > 0 ? BankTransactionTypeCode.IncomingPayment : BankTransactionTypeCode.OutgoingPayment;
    };

    onBeforeSave = () => {
        this.props.storage.clearEmptyLineItems("Transactions");

        const entity = cloneDeep(this.props.storage.data.entity) as IBankStatementEntity;
        delete entity.Balance;

        // for new items add currency
        const transactionCurrencyCode = entity.BankAccount?.TransactionCurrencyCode;
        for (let i = 0; i < (entity.Transactions || []).length; i++) {
            const transaction = entity.Transactions[i];
            if (!transaction.TransactionCurrencyCode) {
                transaction.TransactionCurrencyCode = transactionCurrencyCode;
            }

            if (!transaction.ExchangeRatePerUnit) {
                transaction.ExchangeRatePerUnit = 1;
            }

            if (!transaction.CurrencyCode) {
                transaction.CurrencyCode = getCompanyCurrency(this.props.storage.context);
            }

            if (!transaction.Company) {
                transaction.Company = { Id: this.context.getCompany().Id };
            }

            const minorUnit = this.entity.BankAccount?.Currency?.MinorUnit ?? DEFAULT_SCALE_CURRENCY_VALUE;
            transaction.Amount = roundToDecimalPlaces(minorUnit, transaction.TransactionAmount * transaction.ExchangeRatePerUnit);

            this.setCorrectTransactionType(transaction);


            if (!transaction.NumberOurs) {
                const newNumber = i + 1;
                transaction.NumberOurs = `${entity.NumberOurs}-${getCorrent6Digit(newNumber)}`;
            }
        }

        return entity;
    };

    renderButtons(): React.ReactElement {
        const isInDialog = this.props.storage.getCustomData().isStatementDialog;
        if (isInDialog) {
            return (
                <ButtonGroup wrap={"wrap"}>
                    <Button hotspotId={"formSave"}
                            onClick={this.handleSaveClick}
                            isDisabled={this.shouldDisableSaveButton()}>
                        {this.props.storage.t("Common:General.Save")}
                    </Button>
                </ButtonGroup>
            );
        }

        return super.renderButtons();
    }
}

export default withPermissionContext(BankStatementsFormView);

export { BankStatementsFormView as BankStatementsFormViewForExtend };